/**
 * useBuddyGroupPreferences — read/write the per-rodeo performance
 * preferences a buddy group's primary cardholder locks in.
 *
 * Wraps GET  /buddy-group-ops/:groupId/preferences/editable and
 *       PUT  /buddy-group-ops/:groupId/preferences.
 *
 * The editable check is the authoritative "can this still change?"
 * signal — it returns false once a draw has been published or finalized
 * for any event in the rodeo. The UI mirrors it (hides the affordance)
 * but never replaces it: the backend re-checks on write, so a draw that
 * runs between page load and submit surfaces as a 409.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buddyGroupOpsKeys } from '../api/extensions/useBuddyGroupOps';
import { buddyGroupRodeosKeys } from './useBuddyGroupRodeos';
import { buddyGroupDetailKeys } from './useBuddyGroup';
import {
  getBuddyGroupPreferencesEditable,
  setBuddyGroupPreferences,
} from '../services/buddy-group.service';
import type {
  PreferencesEditableResponse,
  SetBuddyGroupPreferencesResponse,
} from '../types/buddy-groups';

export const buddyGroupPreferencesKeys = {
  editable: (groupId: string, rodeoId: string) =>
    [...buddyGroupOpsKeys.all, 'preferences-editable', groupId, rodeoId] as const,
};

/**
 * Whether the group's preferences for this rodeo can still be changed.
 * Disabled (and therefore never fetched) when the caller has no reason
 * to ask — e.g. the viewer isn't the primary cardholder.
 */
export function useBuddyGroupPreferencesEditable(
  groupId: string | undefined | null,
  rodeoId: string | undefined | null,
  enabled = true,
) {
  return useQuery<PreferencesEditableResponse>({
    queryKey: buddyGroupPreferencesKeys.editable(groupId ?? '', rodeoId ?? ''),
    queryFn: () =>
      getBuddyGroupPreferencesEditable(groupId as string, rodeoId as string),
    enabled: !!groupId && !!rodeoId && enabled,
    staleTime: 60 * 1000,
  });
}

interface SetPreferencesVariables {
  groupId: string;
  rodeoId: string;
  preferences_first: string;
  preferences_second: string | null;
}

/**
 * Save the group's preferences for one rodeo. On success every surface
 * that renders them is invalidated: the group's rodeo list (where the
 * values are displayed), the group detail (legacy mirrored columns), and
 * the editable check itself.
 */
export function useSetBuddyGroupPreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    SetBuddyGroupPreferencesResponse,
    Error,
    SetPreferencesVariables
  >({
    mutationFn: ({ groupId, rodeoId, preferences_first, preferences_second }) =>
      setBuddyGroupPreferences(groupId, rodeoId, {
        preferences_first,
        preferences_second,
      }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: buddyGroupRodeosKeys.forGroup(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: buddyGroupDetailKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: buddyGroupPreferencesKeys.editable(
          variables.groupId,
          variables.rodeoId,
        ),
      });
    },
  });
}
